import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { FaPlay } from "react-icons/fa6";
import { IoBookOutline } from "react-icons/io5";

// MUI
import Button from "@mui/material/Button";

// Components
import Spinner from "../../components/spinner";
import GradedDate from "../../components/GradedDate";
import RoadMapItem from "./RoadMapItem";

// Contexts

// Utils
import { courseRoadMapUrl } from "../../utils/urls";

// Styles
import styles from "./roadmap.module.scss";

// Assets
import star1 from "./assets/star1.svg";
import star2 from "./assets/star2.svg";
import CalendarPng from "../../components/assets/calendar.png";
import { KeyDate, Unit, UnitState } from "../../types";
import { parseRoadMapItems } from "../../parsers";
import Link from "@mui/material/Link";

interface RoadMapProps {
  courseId: string | null;
  reduced: boolean;
}

const RoadMap = ({ courseId = null, reduced = false }: RoadMapProps) => {
  // Hooks
  const { t } = useTranslation();

  // State
  const [loading, setLoading] = useState(true);
  const [roadMap, setRoadMap] = useState<Unit[]>([]);
  const [keyDate, setKeyDate] = useState<KeyDate | null>(null);

  // Refs for auto-scrolling
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const loadRoadMap = useCallback(async () => {
    setLoading(true);
    if (!courseId) return;

    try {
      const options = { method: "GET", headers: { "Accept": "application/json" } };

      const response = await fetch(courseRoadMapUrl(courseId), options);
      if (response.ok) {
        const data = await response.json();
        const parsedItems = parseRoadMapItems(data.map);
        setRoadMap(parsedItems);
        setKeyDate(data.key_date);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  // Find latest unlocked module index
  const latestUnlockedIndex = roadMap.findIndex(item => item.state === 'unlocked');

  // Auto-scroll to latest unlocked module
  useEffect(() => {
    if (latestUnlockedIndex !== -1 && !reduced && !loading) {
      const timer = setTimeout(() => {
        itemRefs.current[latestUnlockedIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100); // Small delay to ensure DOM is ready

      return () => clearTimeout(timer);
    }
  }, [roadMap, latestUnlockedIndex, reduced, loading]);

  // Effects
  useEffect(() => {
    loadRoadMap();
  }, [loadRoadMap]);

  // Render
  const renderRoadMap = () => {
    if (!roadMap) return null;
    if (roadMap.length === 0) return <div>{t("roadMap.no_road_map")}</div>;

    return (
      <div className={`${styles.map} ${reduced ? styles.reducedMap : ''}`} >
        {roadMap.map((item, index) => {
          const hasNext = roadMap.length > index + 1;
          const nextStatus = hasNext ? roadMap[index + 1].content_material.completion_status : null;
          return (
            <RoadMapItem
              key={item.id}
              ref={el => itemRefs.current[index] = el}
              index={index}
              item={item}
              nextStatus={nextStatus}
            />
          );
        })}
      </div>
    );
  };

  const renderKeyDate = () => {
    if (reduced) return null;
    if (!keyDate) return null;

    return (
      <div className={`${styles.keyDateContainer}`}>
        <GradedDate keyDate={keyDate} />
      </div>
    )
  };

  const renderFooter = () => {
    if (reduced) return null;
    const continueJourneyUrl = roadMap.find(item => item.state == UnitState.Unlocked)?.content_material.url || '';

    return (
      <div className={`${styles.footerContainer}`}>
        <Link href={continueJourneyUrl + '?back_path=' + window.location.pathname} className={`${styles.footerButton}`}>
          <Button variant='contained' color='primary' className={`${styles.footerButton}`} size='large'>
            <FaPlay className={`${styles.footerButtonIcon}`} />
            {t("roadMap.continue_journey")}
          </Button>
        </Link>

        <Button variant='text' color='primary' className={`${styles.footerButton} border-none`} size='large'>
          <IoBookOutline className={`${styles.footerButtonIcon}`} />
          {t("roadMap.learn_material")}
        </Button>
      </div>
    )
  }

  return (
    <div className={`${styles.container} ${reduced ? styles.reduced : ''}`} >
      {loading && <Spinner />}

      {renderRoadMap()}

      {/* Key dates are not used for now */}
      {/* {renderKeyDate()} */}

      {renderFooter()}
    </div>
  );
};

export default RoadMap;
